import { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface Props {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack)
  }

  reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6">
        <div className="max-w-md w-full rounded-2xl border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900/50 p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-red-900 dark:text-red-100">
                Something went wrong
              </h2>
              <p className="text-xs text-red-700 dark:text-red-300/80">
                The page hit an unexpected error. You can try again or reload.
              </p>
            </div>
          </div>

          <pre className="mt-4 max-h-32 overflow-auto rounded-md bg-red-100/60 dark:bg-red-900/30 px-3 py-2 text-xs text-red-900 dark:text-red-200 font-mono">
            {error.message || String(error)}
          </pre>

          <div className="mt-5 flex items-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-red-700 transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md border border-red-300 dark:border-red-800 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    )
  }
}
